import * as React from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { cn } from "./utils";

function generateCalendarDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const days: { day: number; currentMonth: boolean }[] = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    days.push({ day: daysInPrevMonth - i, currentMonth: false });
  }

  for (let i = 1; i <= daysInMonth; i++) {
    days.push({ day: i, currentMonth: true });
  }

  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push({ day: i, currentMonth: false });
  }

  return days;
}

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

interface DatePickerProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export function DatePicker({ value, onChange, placeholder = "Selecione uma data", className }: DatePickerProps) {
  const [open, setOpen] = React.useState(false);

  // Extrai data e hora diretamente da string (sem new Date para evitar fuso)
  const dateParts = React.useMemo(() => {
    if (!value) return { year: null, month: null, day: null, hours: "12", minutes: "00" };
    // Espera formato: YYYY-MM-DDTHH:mm ou YYYY-MM-DDTHH:mm:ss
    const [datePart, timePart] = value.split("T");
    if (!datePart) return { year: null, month: null, day: null, hours: "12", minutes: "00" };

    const [yearStr, monthStr, dayStr] = datePart.split("-").map(Number);
    const time = timePart || "12:00";
    const [hours, minutes] = time.split(":");

    return {
      year: yearStr,
      month: monthStr - 1, // JS months are 0-indexed
      day: dayStr,
      hours: hours || "12",
      minutes: (minutes || "00").slice(0, 2), // Remove seconds if present
    };
  }, [value]);

  const [currentMonth, setCurrentMonth] = React.useState(
    dateParts.month !== null ? dateParts.month : new Date().getMonth()
  );
  const [currentYear, setCurrentYear] = React.useState(
    dateParts.year !== null ? dateParts.year : new Date().getFullYear()
  );
  const [time, setTime] = React.useState(
    `${dateParts.hours}:${dateParts.minutes}`
  );
  const [view, setView] = React.useState<"calendar" | "month" | "year">("calendar");

  // Sync when value changes externally
  React.useEffect(() => {
    if (dateParts.year !== null && dateParts.month !== null) {
      setCurrentYear(dateParts.year);
      setCurrentMonth(dateParts.month);
      setTime(`${dateParts.hours}:${dateParts.minutes}`);
    }
  }, [dateParts]);

  const days = React.useMemo(() => generateCalendarDays(currentYear, currentMonth), [currentYear, currentMonth]);

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  const emitValue = (year: number, month: number, day: number, currentTime: string, close = true) => {
    const monthStr = String(month + 1).padStart(2, "0");
    const dayStr = String(day).padStart(2, "0");
    onChange(`${year}-${monthStr}-${dayStr}T${currentTime}`);
    if (close) setOpen(false);
  };

  const selectDay = (day: number, isCurrentMonth: boolean) => {
    let month = currentMonth;
    let year = currentYear;

    if (!isCurrentMonth) {
      if (day > 15) {
        month = currentMonth === 0 ? 11 : currentMonth - 1;
        year = currentMonth === 0 ? currentYear - 1 : currentYear;
      } else {
        month = currentMonth === 11 ? 0 : currentMonth + 1;
        year = currentMonth === 11 ? currentYear + 1 : currentYear;
      }
    }

    emitValue(year, month, day, time, true);
  };

  const handleTimeChange = (newTime: string) => {
    setTime(newTime);
    // Se já existe uma data selecionada, atualiza o valor em tempo real sem fechar
    if (dateParts.year !== null && dateParts.month !== null && dateParts.day !== null) {
      const year = dateParts.year;
      const month = dateParts.month;
      const day = dateParts.day;
      const monthStr = String(month + 1).padStart(2, "0");
      const dayStr = String(day).padStart(2, "0");
      onChange(`${year}-${monthStr}-${dayStr}T${newTime}`);
    }
  };

  const handleConfirm = () => {
    // Se tem data selecionada, confirma e fecha
    if (dateParts.year !== null && dateParts.month !== null && dateParts.day !== null) {
      emitValue(dateParts.year, dateParts.month, dateParts.day, time, true);
    } else {
      setOpen(false);
    }
  };

  const selectMonth = (month: number) => {
    setCurrentMonth(month);
    setView("calendar");
  };

  const selectYear = (year: number) => {
    setCurrentYear(year);
    setView("month");
  };

  const isToday = (day: number, isCurrentMonth: boolean) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      isCurrentMonth &&
      currentMonth === today.getMonth() &&
      currentYear === today.getFullYear()
    );
  };

  const isSelected = (day: number, isCurrentMonth: boolean) => {
    if (dateParts.year === null || dateParts.month === null || dateParts.day === null) return false;

    let month = currentMonth;
    let year = currentYear;

    if (!isCurrentMonth) {
      if (day > 15) {
        month = currentMonth === 0 ? 11 : currentMonth - 1;
        year = currentMonth === 0 ? currentYear - 1 : currentYear;
      } else {
        month = currentMonth === 11 ? 0 : currentMonth + 1;
        year = currentMonth === 11 ? currentYear + 1 : currentYear;
      }
    }

    return (
      day === dateParts.day &&
      month === dateParts.month &&
      year === dateParts.year
    );
  };

  const formatDisplayDate = () => {
    if (dateParts.year === null) return placeholder;
    const { year, month, day, hours, minutes } = dateParts;
    return `${String(day).padStart(2, "0")}/${String(month! + 1).padStart(2, "0")}/${year} ${hours}:${minutes}`;
  };

  const years = React.useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 13 }, (_, i) => current - 5 + i);
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white focus:outline-none focus:ring-2 focus:ring-[#D4C070] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <span className={cn(dateParts.year === null && "text-gray-400", "truncate mr-2")}>
            {dateParts.year !== null ? formatDisplayDate() : placeholder}
          </span>
          <CalendarIcon className="h-4 w-4 text-gray-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 min-w-[280px] sm:min-w-[300px]" align="start" sideOffset={8}>
        {view === "calendar" && (
          <div className="p-3">
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                onClick={prevMonth}
                className="p-1 hover:bg-gray-100 rounded text-lg leading-none"
              >
                ‹
              </button>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setView("month")}
                  className="text-sm font-semibold hover:bg-gray-100 px-2 py-1 rounded"
                >
                  {MONTHS[currentMonth]}
                </button>
                <button
                  type="button"
                  onClick={() => setView("year")}
                  className="text-sm font-semibold hover:bg-gray-100 px-2 py-1 rounded"
                >
                  {currentYear}
                </button>
              </div>
              <button
                type="button"
                onClick={nextMonth}
                className="p-1 hover:bg-gray-100 rounded text-lg leading-none"
              >
                ›
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {WEEKDAYS.map((d) => (
                <div
                  key={d}
                  className="text-center text-xs font-medium text-gray-400 py-1"
                >
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {days.map((d, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => d.currentMonth && selectDay(d.day, d.currentMonth)}
                  className={cn(
                    "h-8 w-8 text-center text-sm rounded-full flex items-center justify-center",
                    !d.currentMonth && "text-gray-300",
                    d.currentMonth && "text-gray-700 hover:bg-gray-100",
                    isToday(d.day, d.currentMonth) && "bg-[#D4C070]/20 font-semibold",
                    isSelected(d.day, d.currentMonth) &&
                    "bg-[#D4C070] text-white hover:bg-[#D4C070] font-semibold"
                  )}
                  disabled={!d.currentMonth}
                >
                  {d.day}
                </button>
              ))}
            </div>
            <div className="border-t pt-3 mt-2">
              <label className="text-xs text-gray-500 block mb-1">Hora</label>
              <input
                type="time"
                value={time}
                onChange={(e) => handleTimeChange(e.target.value)}
                className="w-full text-sm border rounded px-2 py-1"
              />
              <button
                type="button"
                onClick={handleConfirm}
                className="w-full mt-2 bg-[#D4C070] hover:bg-[#849D70] text-white text-sm font-medium py-1.5 rounded transition-colors"
              >
                Confirmar
              </button>
            </div>
          </div>
        )}

        {view === "month" && (
          <div className="p-3">
            <div className="grid grid-cols-3 gap-2">
              {MONTHS.map((month, i) => (
                <button
                  key={month}
                  type="button"
                  onClick={() => selectMonth(i)}
                  className={cn(
                    "p-2 text-sm rounded hover:bg-gray-100",
                    i === currentMonth && "bg-[#D4C070] text-white hover:bg-[#D4C070]"
                  )}
                >
                  {month}
                </button>
              ))}
            </div>
          </div>
        )}

        {view === "year" && (
          <div className="p-3">
            <div className="grid grid-cols-3 gap-2 max-h-60 overflow-y-auto">
              {years.map((year) => (
                <button
                  key={year}
                  type="button"
                  onClick={() => selectYear(year)}
                  className={cn(
                    "p-2 text-sm rounded hover:bg-gray-100",
                    year === currentYear && "bg-[#D4C070] text-white hover:bg-[#D4C070]"
                  )}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
